/*!

=========================================================
* Central System - v1.0.0
=========================================================

* Coded by Aircode

=========================================================

*/
import React, { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { postOrder, captureOrder, postSubscription, postPayment } from "../../../../network/ApiAxios";
import { useNavigate } from 'react-router-dom';
import secureLocalStorage from "react-secure-storage";

// Renders errors or successfull transactions on the screen.
function Message({ content }) {
    return <p>{content}</p>;
}

const PayPalPayments = (props) => {

    const initialOptions = {
        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
        "enable-funding": "venmo",
        currency: "USD",
        components: "buttons",
        "disable-funding": "paylater", // This line disables the Pay Later button
    };

    const [message, setMessage] = useState("");
    const user = JSON.parse(secureLocalStorage.getItem("user"));
    const navigate = useNavigate();

    /**
     * Cria a ordem de pagamento no PayPal com os dados
     * do plano configurado pelo usuário. Essa etapa é
     * controlada pela interface do PayPal, onde o usuário
     * precisa fazer login, enquanto o Sistema Central fica
     * em espera
     * 
     * @returns 
     */
    let createOrder = async () => {
        try {

            const cart = {
                cart:
                {
                    selectedPlan: props.selectedPlan,
                    selectedProduct: props.selectedProduct,
                },
            }
            
            const response = await postOrder(cart);
            const orderData = response.data

            if (orderData.id) {
                return orderData.id;
            } else {
                const errorDetail = orderData?.details?.[0];
                const errorMessage = errorDetail
                    ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                    : JSON.stringify(orderData);

                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error(error);
            setMessage(`Could not initiate PayPal Checkout...${error}`);
        }
    }

    /**
     * Detecta se a ordem foi paga pelo usuário na interface do PayPal
     * e obtém os dados deste pagamento. Ao final, redireciona a página
     * para a tela inicial do Sistema Central
     * 
     * @param {*} data 
     * @param {*} actions 
     * @returns 
     */
    let onApprove = async (data, actions) => {
        try {
            const response = await captureOrder(data.orderID)
            const orderData = response.data
            // Three cases to handle:
            //   (1) Recoverable INSTRUMENT_DECLINED -> call actions.restart()
            //   (2) Other non-recoverable errors -> Show a failure message
            //   (3) Successful transaction -> Show confirmation or thank you message

            const errorDetail = orderData?.details?.[0];

            if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
                // (1) Recoverable INSTRUMENT_DECLINED -> call actions.restart()
                // recoverable state, per https://developer.paypal.com/docs/checkout/standard/customize/handle-funding-failures/
                return actions.restart();
            } else if (errorDetail) {
                // (2) Other non-recoverable errors -> Show a failure message
                throw new Error(
                    `${errorDetail.description} (${orderData.debug_id})`
                );
            } else {
                // (3) Successful transaction -> Show confirmation or thank you message
                // Or go to another URL:  actions.redirect('thank_you.html');
                const transaction =
                    orderData.purchase_units[0].payments.captures[0];
                setMessage(
                    `Transaction ${transaction.status}: ${transaction.id}. See console for all available details`
                );
                //console.log(
                //    "Capture result",
                //    orderData,
                //    JSON.stringify(orderData, null, 2)
                //);

                //Fazemos os registros e vinculos do pagamento e assinatura
                //no Sistema Central                
                let wasDataStored = await createSubscription(orderData)

                if(wasDataStored){
                    //Limpar os caches de dados temporários de configuração
                    //de plano
                    localStorage.setItem('planSelectionData', null)
                    localStorage.setItem('selectedPlan', null)
                    localStorage.setItem('selectedProduct', null)

                    //redirect para a tela inicial do Sistema Central 
                    navigate("/admin");
                }
            }
        } catch (error) {
            console.error(error);
            setMessage(
                `Sorry, your transaction could not be processed...${error}`
            );
        }
    }

    /**
        * Registra os dados da ordem após o pagamento realizado pelo usuário
        * no sistema do PayPal
        * 
        * @param {*} req 
        * @param {*} res 
        */
    let storePayments = async (paymentData, subscriptionID) => {
        const runAsync = async (paymentData, subscriptionID) => {

            const payment = {
                orderId: paymentData.id,
                account: user.account,
                subscription: subscriptionID,
                itemPrice: props.selectedPlan.price,
                discount: props.selectedPlan.discount,
                total: props.selectedPlan.discount ? props.selectedPlan.price - props.selectedPlan.discount : props.selectedPlan.price,
                fromPayPal: true,
                app: props.selectedProduct.id,
                raw: JSON.stringify(paymentData),
            }
            
            const response = await postPayment(payment);
            const { data } = response;
            
            if (data.success) {
                return true
            } else {
                throw new Error(data.msg)
            }
        }

        return runAsync(paymentData, subscriptionID);
    }

    /**
     * Através dos dados da ordem após o pagamento realizado pelo usuário
     * no sistema do PayPal, registra os dados de pagamento no Sistema Central
     * e cria uma assinatura para a conta no aplicativo comprado
     * 
     * @param {*} req 
     * @param {*} res 
     */
    let createSubscription = async (paymentData) => {
        const runAsync = async (paymentData) => {

            const subscription = {
                account: user.account,
                licensesLimit: props.selectedPlan.licensesCount,
                app: props.selectedProduct.id,
                periodicity: props.selectedPlan.periodicity,
            }

            const response = await postSubscription(subscription);
            const { data } = response;

            if (data.success) {
                let result = await storePayments(paymentData, data.subscriptionID)                
                return result
            } else {
                throw new Error(data.msg)
            }
        }

        return runAsync(paymentData);
    }    

    return (
        <div className="paypal-button-container">            
            <PayPalScriptProvider options={initialOptions}>
                <PayPalButtons
                    style={{
                        shape: "rect",
                        layout: "vertical",
                        color: "gold",
                        label: "paypal",
                    }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                />
            </PayPalScriptProvider>
            <Message content={message} />
        </div>
    );
}

export default PayPalPayments;